import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CustomDelete, EditWithIcon, NotError, SimpleTooltip, TagComponent, TypeRow, UserInputsDropdown } from 'components';
import {
    FindErrorItem,
    goalDataTypeEnums,
    Images,
    PermissionList,
    renderGoalTypes,
    RolePermission,
    tagEnums,
    useModal,
    useSecondaryModal,
} from 'utils';
import {
    convertGoalEnums,
    DELETE_GOAL_ACTION_TYPE,
    goalStatusEnums,
    goalStatuses,
    CREATE_LIBRARY_GOAL_ACTION_TYPE,
    teachingMethodEnums,
} from '../constants';
import { AddGoal } from '../addGoal';
import { goalActions, httpRequestsOnErrorsActions, libraryActions } from 'store';
import { toast } from 'react-toastify';

const ACTION_TYPE = 'EDIT_GOAL_STATUS';
export const GoalDetailsHeader = ({ requestParams, afterSuccess }) => {
    const goalById = useSelector((state) => state.goals.goalById);
    const dispatch = useDispatch();
    const { open, close } = useModal();
    const { closeSecondary } = useSecondaryModal();
    const saveToLibraryError = FindErrorItem(CREATE_LIBRARY_GOAL_ACTION_TYPE);

    useEffect(() => {
        if (saveToLibraryError) {
            const message =
                saveToLibraryError?.error === 'Goal conflict: coincident record found'
                    ? 'A goal with this name already exists in Library Goals.'
                    : saveToLibraryError?.error || 'Failed to save goal to library.';
            toast(<NotError type={'error'} errMessage={message} />);
            dispatch(httpRequestsOnErrorsActions.removeError(CREATE_LIBRARY_GOAL_ACTION_TYPE));
        }
    }, [saveToLibraryError]);

    const renderStatusDisabledCases = (goalStatus) => {
        if (goalStatus === goalStatusEnums.ACTIVE || goalStatus === goalStatusEnums.MASTERED) {
            return !goalById?.targets?.length;
        }
        return false;
    };

    // Reuses Library → "Add New Goal" (POST /clinical/library/goals): only the goal settings carry over.
    const handleSaveToLibrary = () => {
        const params = {
            name: goalById?.name,
            type: goalById?.type,
            dataType: goalById?.dataType,
            teachingMethod: goalById?.teachingMethod,
            minTrials: Math.max(+goalById?.minTrials || 0, 1),
        };
        if (goalById?.description) params.description = goalById.description;
        if (goalById?.domain?.id) params.domain = goalById.domain.id;
        if (goalById?.teachingMethod === teachingMethodEnums.TASK_ANALYSIS && goalById?.TAMode) params.TAMode = goalById.TAMode;
        if (goalById?.maxTrials) params.maxTrials = +goalById.maxTrials;
        if (goalById?.dataType === goalDataTypeEnums.RATE && goalById?.timeUnit) params.timeUnit = goalById.timeUnit;

        dispatch(libraryActions.createLibraryGoal(params));
    };

    const handleChangeStatus = (status) => {
        dispatch(goalActions.editGoalStatus(goalById?.id, status, requestParams));
    };

    return (
        <div>
            <div className="space-between-flex-start">
                <div className="goal-detail-name-type-box">
                    <p>{goalById?.name}</p>
                    {goalById?.domain?.name && <div>{goalById?.domain?.name}</div>}
                </div>

                <div className="flex-align-center" style={{ gap: 12 }}>
                    <TagComponent
                        userInfo={goalById}
                        module={tagEnums.GOAL}
                        singleView
                        maxTags={null}
                        notSetText={'No tags added'}
                        afterSuccess={afterSuccess}
                    />
                    {RolePermission([PermissionList.GOAL_CREATE?.code]) && (
                        <UserInputsDropdown
                            styles={{ width: 170 }}
                            dropdownOptions={goalStatuses}
                            actionType={ACTION_TYPE}
                            selected={goalById?.status}
                            onPass={handleChangeStatus}
                            type={'goalStatuses'}
                            disableRow={(item) => renderStatusDisabledCases(item?.id)}
                            notifyMessage={'Please add at least one target before continuing.'}
                        />
                    )}
                    {RolePermission([PermissionList.GOAL_CREATE?.code]) && (
                        <SimpleTooltip title={<p>Save to Library Goals</p>} placement="top">
                            <div>
                                <EditWithIcon
                                    id={'save-goal-to-library-btn'}
                                    icon={Images.copy}
                                    type={'button'}
                                    handleClick={() =>
                                        open(
                                            <CustomDelete
                                                info={`Save "${goalById?.name}" to Library Goals?`}
                                                text={
                                                    <>
                                                        A new goal with the same name and settings will be added to <b>Library Goals</b>.
                                                        Targets, options, mastery criteria and tags are not copied.
                                                    </>
                                                }
                                                innerText={'Save to Library'}
                                                color={'blue'}
                                                actionType={CREATE_LIBRARY_GOAL_ACTION_TYPE}
                                                handleDel={handleSaveToLibrary}
                                                afterSuccess={() => toast(<NotError errMessage={'Goal saved to Library Goals'} />)}
                                            />
                                        )
                                    }
                                />
                            </div>
                        </SimpleTooltip>
                    )}
                    {RolePermission([PermissionList.GOAL_CREATE?.code]) && (
                        <EditWithIcon
                            icon={Images.editAuth}
                            type={'button'}
                            handleClick={() => open(<AddGoal requestParams={requestParams} info={goalById} />)}
                        />
                    )}
                    {RolePermission([PermissionList.GOAL_CREATE?.code]) && (
                        <EditWithIcon
                            icon={Images.deleteTag}
                            type={'button'}
                            handleClick={() =>
                                open(
                                    <CustomDelete
                                        info={`Are you sure you want to delete "${goalById?.name}"?`}
                                        text={
                                            <>
                                                This goal will be moved to Deleted Items and <b>won't be available for new sessions</b>.
                                                Existing session data will remain unchanged. You can view deleted goals using the{' '}
                                                <b>"Show deleted"</b> toggle.
                                            </>
                                        }
                                        innerText={'Delete'}
                                        actionType={DELETE_GOAL_ACTION_TYPE}
                                        handleDel={() => dispatch(goalActions.deleteGoal(goalById?.id, requestParams))}
                                        afterSuccess={() => {
                                            closeSecondary();
                                            close();
                                        }}
                                    />
                                )
                            }
                        />
                    )}
                </div>
            </div>

            <div className="goal-detail-information">
                <div>
                    <p style={{ fontWeight: 600 }}>Mode: </p>
                    <p>{renderGoalTypes(goalById?.dataType)}</p>
                </div>
                <div>
                    <p style={{ fontWeight: 600 }}>Teaching Method: </p>
                    <p>{convertGoalEnums(goalById?.teachingMethod)}</p>
                </div>
                {goalById?.teachingMethod === teachingMethodEnums.TASK_ANALYSIS && (
                    <div>
                        <p style={{ fontWeight: 600 }}>Chaining: </p>
                        <p>{convertGoalEnums(goalById?.TAMode)}</p>
                    </div>
                )}
                <div>
                    <p style={{ fontWeight: 600 }}>Type: </p>
                    <p>{convertGoalEnums(goalById?.type)}</p>
                </div>
                <div>
                    <p style={{ fontWeight: 600 }}>Min Trials: </p>
                    <p>{goalById?.minTrials}</p>
                </div>
                <div>
                    <p style={{ fontWeight: 600 }}>Max Trials: </p>
                    <p>{goalById?.maxTrials ?? 'No limit'}</p>
                </div>

                {goalById?.dataType === goalDataTypeEnums.RATE && (
                    <div>
                        <p style={{ fontWeight: 600 }}>Rate Measured Per: </p>
                        <p>
                            <TypeRow text={goalById?.timeUnit} />
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};
